import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, ArrowRight, Crown } from "lucide-react";
import GoldenParticles from "@/components/GoldenParticles";
import WaveGoldDivider from "@/components/WaveGoldDivider";
import VIPBadge from "@/components/VIPBadge";
import AnimatedSection from "@/components/AnimatedSection";

const TransferDestinations = () => {
  const { data: destinations, isLoading } = useActiveTransferDestinations();

  return (
    <Layout>
      <Helmet>
        <title>VIP Transfer Destinations | TROPUNCA VIP Premium Transportation</title>
        <meta
          name="description"
          content="Discover our VIP transfer destinations in Dominican Republic. Premium service, competitive prices, and 24/7 availability."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-32 bg-vip-dark overflow-hidden">
        <GoldenParticles count={25} />
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
              Premium Destinations
            </VIPBadge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
              <span className="text-gradient-gold">VIP Transfer</span> Destinations
            </h1>
            <p className="text-xl text-white/80">
              Premium VIP transportation to the best destinations in Dominican Republic
            </p>
          </AnimatedSection>
        </div>
        
        <WaveGoldDivider className="absolute bottom-0 left-0 right-0" dark />
      </section>

      {/* Destinations Grid */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-12">
              <Crown className="w-12 h-12 animate-pulse-glow text-primary mb-4" />
              <p className="text-muted-foreground">Loading VIP destinations...</p>
            </div>
          ) : destinations && destinations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((destination, index) => (
                <AnimatedSection key={destination.id} delay={index * 100}>
                  <Card className="overflow-hidden hover:shadow-gold transition-all duration-500 group border-transparent hover:border-primary/30 hover:-translate-y-2">
                    {destination.hero_image && (
                      <div className="aspect-video overflow-hidden relative">
                        <img
                          src={destination.hero_image}
                          alt={destination.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <VIPBadge variant="exclusive" size="sm" className="absolute top-4 right-4">
                          VIP
                        </VIPBadge>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <h2 className="text-2xl font-bold mb-2 text-gradient-gold">{destination.name}</h2>
                      {destination.description && (
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {destination.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-4">
                        {destination.estimated_time && (
                          <Badge variant="outline" className="border-primary/50 text-primary">
                            <Clock className="h-4 w-4 mr-1" />
                            {destination.estimated_time}
                          </Badge>
                        )}
                        {destination.from_airport_price && (
                          <Badge variant="outline" className="border-primary/50 text-primary">
                            <DollarSign className="h-4 w-4 mr-1" />
                            From ${destination.from_airport_price}
                          </Badge>
                        )}
                      </div>

                      {destination.highlights && destination.highlights.length > 0 && (
                        <ul className="space-y-1 mb-4">
                          {destination.highlights.slice(0, 3).map((highlight, hIndex) => (
                            <li key={hIndex} className="text-sm text-muted-foreground flex items-start">
                              <span className="text-primary mr-2">✓</span>
                              {highlight}
                            </li>
                          ))}
                        </ul>
                      )}

                      <Link to={`/traslados/${destination.slug}`}>
                        <Button className="w-full bg-gradient-gold text-white hover:opacity-90 shadow-gold group">
                          View Details
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No destinations available at the moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={15} />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <Crown className="h-16 w-16 mx-auto mb-6 text-primary animate-float" />
            <h2 className="text-4xl font-bold mb-4 text-white">
              Can't Find Your <span className="text-gradient-gold">Destination</span>?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Contact our VIP concierge team and we'll help you organize your premium transfer to any destination in Dominican Republic
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group px-8">
                Contact VIP Concierge
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default TransferDestinations;
